import { memo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Edit, User, Bot } from "lucide-react";
import { MessageEditorCard } from "./MessageEditorCard";
import ReactMarkdown from "react-markdown";
import { Message } from "@/types/message";

interface MessageBubbleProps {
  message: Message;
  onEdit: (messageId: string) => void;
  isEditing: boolean;
  onCancelEdit: () => void;
  onSaveEdit: (content: string) => void;
  onSummarize: (summary: string) => void;
  onSend: (summary: string) => void;
}

const MessageBubbleComponent = ({ 
  message, 
  onEdit, 
  isEditing,
  onCancelEdit,
  onSaveEdit,
  onSummarize,
  onSend
}: MessageBubbleProps) => {
  const isAI = message.role === "assistant";

  if (isEditing) {
    return (
      <div className="animate-accordion-down" id={`message-${message.id}`}>
        <MessageEditorCard
          message={message}
          onCancel={onCancelEdit}
          onSave={(content, changes, comments) => onSaveEdit(content)}
          onSummarize={onSummarize}
          onSend={onSend}
        />
      </div>
    );
  }

  return (
    <Card className="p-4 relative group max-w-3xl transition-all duration-300" id={`message-${message.id}`}>
      <div className="flex gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isAI ? "bg-primary/10" : "bg-muted"
        }`}>
          {isAI ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-2">
            {isAI ? "AI Assistant" : "You"}
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(message.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Edit className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

// Memoize to prevent unnecessary re-renders
export const MessageBubble = memo(MessageBubbleComponent, (prevProps, nextProps) => {
  // Only re-render if these specific properties change
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.isEditing === nextProps.isEditing
  );
});
